import type { ShoppingHistoryEntry, AiProfile } from '../../types';
import { normalizeToSubUnit } from './intelligence';

/**
 * Restock engine behind Restock Radar (dashboard) and Suggested Essentials
 * (basket). It works out which things the user actually rebuys and when each
 * one is next due, from their own purchase history.
 *
 * Three ideas keep it accurate for an any-store, any-category app:
 *
 * 1. Families, not products. Every brand and size of eggs is one "eggs"
 *    family, so switching brands doesn't break the buying rhythm. The family
 *    comes from the AI at extraction (metadata.family); older items fall back
 *    to their English `product` field, matched cautiously (familyNoun).
 * 2. Replenishable, not grocery. A TV or a drill bought twice is not a
 *    restock; ink, pet food or detergent is (classifyReplenishable).
 * 3. Evidence before suggestions. A family is tracked only once it shows a
 *    buying rhythm, and drops off when the user stops buying it.
 *
 * Everything here is a pure function of its inputs, so it can be checked in
 * isolation: `npm run check:restock` (scripts/check_restock.mjs).
 */

export type Replenishable = 'yes' | 'no' | 'maybe';
export type RestockConfidence = 'high' | 'medium' | 'low';

export interface RestockItem {
  /** Family key ("family:<noun>"). Hide and snooze are stored under it. */
  key: string;
  /** Normalized family noun, e.g. "egg", "printer ink". */
  family: string;
  /** item_data keys of every product in the family (hide/snooze also store these). */
  memberKeys: string[];
  /** How many different products (brands/sizes) the family has. */
  variantCount: number;
  /** The most recently bought product: what gets added to the basket. */
  name: string;
  category: string;
  replenishable: Replenishable;
  confidence: RestockConfidence;
  /** Distinct days the family was bought on. */
  purchaseDays: number;
  /** Typical number of days between purchases. */
  intervalDays: number;
  lastPurchaseDate: string;
  dueDate: string;
  /** Whole days until due; negative when overdue. */
  daysUntilDue: number;
  /** 1 right after a purchase, 0 when due. */
  level: number;
  /** Personal Best: the lowest price paid (per unit when sizes compare), and where. */
  bestPrice: number;
  bestStore: string;
  /** Every store the user bought the family at. */
  stores: string[];
  imageUrl?: string;
  canonicalId?: string;
  brand?: string | null;
  product?: string | null;
  variety?: string | null;
  size?: string | number | null;
  unit?: string | null;
  upc?: string | null;
  gtin?: string | null;
}

/** profiles.settings.restock */
export interface RestockSettings {
  /** Don't suggest again (key -> true). Keys are family keys or item_data keys. */
  hidden?: Record<string, boolean | null>;
  /** Snoozed (key -> ISO time the snooze ends). Same keys as hidden. */
  snoozed?: Record<string, string | null>;
}

/** What the AI said about a product at extraction (getRestockSignals). */
export interface RestockSignal {
  slug: string;
  family?: string | null;
  replenishable?: boolean | null;
}

export const SNOOZE_DAYS = 7;
/** Due within this many days counts as "due" for the basket. */
export const BASKET_DUE_WINDOW_DAYS = 3;
export const BASKET_LIMIT = 5;
export const DASHBOARD_LIMIT = 8;

// Lines on a receipt that aren't products: fees, deposits, services.
const NON_GOODS = /\b(fees?|deposit|crv|tax|gratuity|surcharge|service charge|delivery|shipping|gift ?cards?|coupon|discount|membership|subscription|warranty|protection plan|repair|labou?r|installation|trading|software|web development|app)\b/i;

// Things bought again and again, whatever store they come from.
const CONSUMABLE_WORDS = /\b(ink|toner|cartridges?|batter(y|ies)|filters?|refills?|razors?|blades?|pods?|capsules?|diapers?|wipes|formula|detergent|soap|shampoo|conditioner|toothpaste|floss|deodorant|tissues?|toilet paper|paper towels?|trash bags?|garbage bags?|sponges?|cleaner|bleach|litter|pet food|dog food|cat food|kibble|vitamins?|supplements?|medication|medicine|contact lens(es)?|coffee|tea|milk|eggs?|bread|water|lotion|sunscreen|pads|tampons?|propane|charcoal|fuel|gasoline)\b/i;

// Categories of everyday consumables.
const CONSUMABLE_CATEGORY = /produce|fruit|vegetable|meat|seafood|poultry|dairy|egg|bakery|bread|grocer|pantry|beverage|drink|snack|frozen|rice|grain|pasta|spice|season|condiment|sauce|deli|cereal|breakfast|canned|household|cleaning|laundry|health|care|pharmacy|personal|beauty|hygiene|baby|pet|food/i;

// Categories of one-off or long-lasting purchases.
const DURABLE_CATEGORY = /electronic|apparel|cloth|shoe|footwear|furniture|automo|vehicle|entertainment|tech|media|toy|game|jewel|appliance|sport|book|decor/i;

// Categories with both (a paint brush and sandpaper): need a proven rhythm.
const MIXED_CATEGORY = /hardware|tool|garden|home improvement|office|craft/i;

const BABY_ONLY = /\b(diapers?|formula|baby food|baby wipes)\b/i;

const DAY_MS = 24 * 60 * 60 * 1000;

const dayNumber = (d: Date) => Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/**
 * Whether a product is something people restock. Fees and services are ruled
 * out first, then the AI's verdict at extraction (metadata.replenishable)
 * wins, then the category and product words decide. Onboarding context
 * refines it: baby-only products stop counting once every child in the
 * profile is 4+.
 */
export const classifyReplenishable = (
  text: { name?: string | null; category?: string | null; slug?: string | null },
  aiVerdict?: boolean | null,
  aiProfile?: AiProfile | null,
  now: Date = new Date(),
): Replenishable => {
  const words = [text.name, text.slug?.replace(/-/g, ' ')].filter(Boolean).join(' ');
  const category = text.category || '';

  const kids = aiProfile?.domains?.dependents?.kids;
  if (BABY_ONLY.test(words) && Array.isArray(kids) && kids.length > 0) {
    const years = kids.map(k => k?.birth_year).filter((y): y is number => typeof y === 'number');
    if (years.length === kids.length && years.every(y => now.getFullYear() - y >= 4)) return 'no';
  }

  if (NON_GOODS.test(words)) return 'no';
  if (aiVerdict === true) return 'yes';
  if (aiVerdict === false) return 'no';
  if (CONSUMABLE_WORDS.test(words)) return 'yes';
  if (DURABLE_CATEGORY.test(category)) return 'no';
  if (MIXED_CATEGORY.test(category)) return 'maybe';
  if (CONSUMABLE_CATEGORY.test(category)) return 'yes';
  return 'maybe';
};

// Words that describe a product without changing what it is ("whole milk"
// is milk, "bread loaf" is bread). Kept short on purpose: a wrong merge
// ("soy sauce" into "sauce") invents a buying rhythm, a missed one only
// leaves a family split until the AI's metadata.family covers it.
const FAMILY_FILLER = new Set(['whole', 'white', 'organic', 'fresh', 'large', 'small', 'medium', 'jumbo', 'hass', 'loaf', 'sliced', 'mini', 'regular']);

const singular = (w: string) =>
  w.endsWith('ies') && w.length > 4 ? `${w.slice(0, -3)}y`
  : /(oes|ches|shes|xes|sses)$/.test(w) ? w.slice(0, -2)
  : w.endsWith('s') && !w.endsWith('ss') && w.length > 3 ? w.slice(0, -1)
  : w;

/**
 * The family noun of a product: lowercase, singular, without brand-free
 * filler words or parenthetical notes. "Eggs", "eggs" and "Large Eggs" all
 * give "egg"; "Avocado Oil" stays "avocado oil".
 */
export const familyNoun = (text: string | null | undefined): string => {
  const words = String(text || '')
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(singular);
  const kept = words.filter(w => !FAMILY_FILLER.has(w));
  return (kept.length ? kept : words).join(' ').trim();
};

/**
 * A product's family noun: the AI's family, else its English `product` type.
 * Older items without a usable product type (missing, or the brand saved in
 * its place) don't guess from the display name, whose leading word is often
 * the brand ("Sunny Select (Bread, …)"): they keep their whole name, so only
 * exact duplicates merge.
 */
const memberNoun = (aiFamily: string | null | undefined, entry: any, name: string): string => {
  if (aiFamily) return familyNoun(aiFamily);
  const product = familyNoun(entry.product);
  if (product && product !== familyNoun(entry.brand)) return product;
  return familyNoun(String(name).replace(/[()]/g, ' '));
};

/** Family key for hide/snooze. Versioned so a future change of scheme can't collide with item keys. */
export const familyKey = (noun: string) => `family:${noun}`;

interface Purchase { memberKey: string; day: number; date: Date; qty: number; price: number; store: string; imageUrl?: string }

interface Member {
  key: string;
  entry: any;
  noun: string;
  replenishable: Replenishable;
  purchases: Purchase[];
  /** Package size in base units (g, ml or ct), when known. */
  base?: { amount: number; unit: string };
}

const packageSize = (entry: any): Member['base'] => {
  const size = typeof entry.size === 'number' ? entry.size : parseFloat(entry.size);
  if (!(size > 0) || !entry.unit) return undefined;
  const { normalizedValue, baseUnit } = normalizeToSubUnit(size, String(entry.unit));
  // "1 ct" is the reader's default when no size is printed: treat it as unknown.
  if (baseUnit === 'ct' && normalizedValue === 1) return undefined;
  return normalizedValue > 0 ? { amount: normalizedValue, unit: baseUnit } : undefined;
};

/** AI signals and images from the loaded trips, as a fallback for getRestockSignals. */
const indexHistory = (history: ShoppingHistoryEntry[]) => {
  const signals = new Map<string, { family?: string; replenishable?: boolean }>();
  const images = new Map<string, string>();
  // history is newest first, so the first value seen is the latest.
  for (const entry of history) {
    for (const item of entry.items || []) {
      const keys = [item.canonicalId, (item as any).canonicalName, item.name].filter(Boolean) as string[];
      const meta = item.metadata || {};
      for (const k of keys) {
        const s = signals.get(k) || {};
        if (s.family === undefined && typeof meta.family === 'string') s.family = meta.family;
        if (s.replenishable === undefined && typeof meta.replenishable === 'boolean') s.replenishable = meta.replenishable;
        signals.set(k, s);
        if (item.imageUrl && !images.has(k)) images.set(k, item.imageUrl);
      }
    }
  }
  return { signals, images };
};

/** Whether hide/snooze settings cover this family (by family key or any member key). */
const settingFor = <T>(map: Record<string, T | null> | undefined, keys: string[]): T | undefined => {
  if (!map) return undefined;
  for (const k of keys) {
    const v = map[k];
    if (v != null) return v;
  }
  return undefined;
};

/**
 * Every family the user has a buying rhythm for, most urgent first.
 * Hidden and snoozed families are left out.
 */
export const computeRestockItems = (
  itemData: Record<string, any> | null | undefined,
  history: ShoppingHistoryEntry[],
  options: {
    settings?: RestockSettings | null;
    aiProfile?: AiProfile | null;
    signals?: RestockSignal[] | null;
    now?: Date;
  } = {},
): RestockItem[] => {
  if (!itemData) return [];
  const now = options.now || new Date();
  const today = dayNumber(now);
  const { signals: historySignals, images } = indexHistory(history);
  const dbSignals = new Map((options.signals || []).map(s => [s.slug, s]));
  const signalFor = (entry: any, key: string, name: string) => {
    const db = dbSignals.get(entry.slug);
    const h = historySignals.get(entry.slug) || historySignals.get(key) || historySignals.get(name) || {};
    return { family: db?.family ?? h.family, replenishable: db?.replenishable ?? h.replenishable };
  };

  // 1. Products with real purchases, classified and given a family noun.
  const members: Member[] = [];
  for (const [key, entry] of Object.entries(itemData)) {
    const storesMap = entry?.stores;
    if (!storesMap || typeof storesMap !== 'object') continue;

    const purchases: Purchase[] = [];
    for (const [store, prices] of Object.entries(storesMap)) {
      if (!Array.isArray(prices)) continue;
      for (const p of prices as any[]) {
        const date = new Date(p.date);
        if (isNaN(date.getTime())) continue;
        purchases.push({ memberKey: key, day: dayNumber(date), date, qty: Number(p.quantity) || 1, price: Number(p.price) || 0, store: store || '', imageUrl: p.imageUrl || undefined });
      }
    }
    // A product only ever recorded at $0 (a sample, a freebie, a misread) isn't one to restock.
    if (purchases.length === 0 || purchases.every(p => p.price <= 0)) continue;

    const name = entry.primary_name || entry.canonical_name || key;
    const signal = signalFor(entry, key, name);
    const replenishable = classifyReplenishable({ name, category: entry.category, slug: entry.slug }, signal.replenishable, options.aiProfile, now);
    if (replenishable === 'no') continue;

    const noun = memberNoun(signal.family, entry, name);
    if (!noun) continue;
    members.push({ key, entry, noun, replenishable, purchases, base: packageSize(entry) });
  }

  // 2. Families.
  const families = new Map<string, Member[]>();
  for (const m of members) families.set(m.noun, [...(families.get(m.noun) || []), m]);

  const hidden = options.settings?.hidden || undefined;
  const snoozed = options.settings?.snoozed || undefined;
  const results: RestockItem[] = [];

  for (const [noun, group] of families) {
    const key = familyKey(noun);
    const keys = [key, ...group.map(m => m.key)];
    if (settingFor(hidden, keys)) continue;
    const snoozeEnd = settingFor(snoozed, keys);
    if (snoozeEnd && new Date(snoozeEnd).getTime() > now.getTime()) continue;

    const replenishable: Replenishable = group.some(m => m.replenishable === 'yes') ? 'yes' : 'maybe';
    const memberByKey = new Map(group.map(m => [m.key, m]));

    // Same product, price and quantity on the same day is one purchase saved
    // twice (two archives of one receipt); different products that day add up.
    const seen = new Set<string>();
    const purchases = group.flatMap(m => m.purchases).filter(p => {
      const id = `${p.memberKey}|${p.day}|${p.qty}|${p.price}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    // How much was bought each day: in package units (g, ml, ct) when every
    // product in the family has a comparable size, otherwise in items.
    const baseUnits = new Set(group.map(m => m.base?.unit));
    const sizeAware = baseUnits.size === 1 && group.every(m => m.base);
    const amountOf = (p: Purchase) => p.qty * (sizeAware ? memberByKey.get(p.memberKey)!.base!.amount : 1);

    const byDay = new Map<number, Purchase[]>();
    for (const p of purchases) byDay.set(p.day, [...(byDay.get(p.day) || []), p]);
    const days = [...byDay.keys()].sort((a, b) => a - b);
    const dayAmount = days.map(d => byDay.get(d)!.reduce((s, p) => s + amountOf(p), 0));

    const gaps = days.slice(1).map((d, i) => d - days[i]);
    // 'maybe' families need a proven rhythm: 3 purchase days. Known consumables need 2.
    if (gaps.length < (replenishable === 'yes' ? 1 : 2)) continue;

    const typicalGap = median(gaps);
    const mean = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    const spread = gaps.length > 1 ? Math.sqrt(gaps.reduce((s, g) => s + (g - mean) ** 2, 0) / gaps.length) / mean : 1;
    if (replenishable === 'maybe' && spread > 0.6) continue;

    const confidence: RestockConfidence =
      gaps.length >= 3 && spread <= 0.35 ? 'high'
      : gaps.length >= 2 && spread <= 0.6 ? 'medium'
      : 'low';

    // Bought more than usual last time (more items, or a bigger pack): it lasts longer.
    const earlier = dayAmount.slice(0, -1);
    const typicalAmount = median(earlier.length ? earlier : dayAmount);
    const lastAmount = dayAmount[dayAmount.length - 1];
    const amountFactor = Math.min(3, Math.max(0.5, typicalAmount > 0 ? lastAmount / typicalAmount : 1));
    const interval = Math.max(1, Math.round(typicalGap * amountFactor));

    const lastDay = days[days.length - 1];
    const daysUntilDue = lastDay + interval - today;
    // Overdue by more than twice the usual gap: the user has stopped buying it.
    if (daysUntilDue < -2 * interval) continue;

    const newestFirst = [...purchases].sort((a, b) => b.date.getTime() - a.date.getTime());
    const latest = newestFirst[0];
    const latestMember = memberByKey.get(latest.memberKey)!;
    const entry = latestMember.entry;

    // Personal Best per unit when sizes compare, otherwise the latest product's lowest price.
    const paid = purchases.filter(p => p.price > 0);
    const candidates = sizeAware ? paid : paid.filter(p => p.memberKey === latest.memberKey);
    const unitCost = (p: Purchase) => (sizeAware ? p.price / memberByKey.get(p.memberKey)!.base!.amount : p.price);
    const best = (candidates.length ? candidates : paid).reduce((a, b) => (unitCost(b) < unitCost(a) ? b : a));

    const name = entry.primary_name || entry.canonical_name || latest.memberKey;
    results.push({
      key,
      family: noun,
      memberKeys: group.map(m => m.key),
      variantCount: group.length,
      name,
      category: entry.category || 'Other',
      replenishable,
      confidence,
      purchaseDays: days.length,
      intervalDays: interval,
      lastPurchaseDate: latest.date.toISOString(),
      dueDate: new Date(latest.date.getTime() + interval * DAY_MS).toISOString(),
      daysUntilDue,
      level: Math.min(1, Math.max(0, daysUntilDue / interval)),
      bestPrice: best.price,
      bestStore: best.store,
      stores: [...new Set(purchases.map(p => p.store).filter(Boolean))],
      imageUrl: entry.image_url || entry.imageUrl || newestFirst.find(p => p.imageUrl)?.imageUrl || images.get(entry.slug) || images.get(name),
      canonicalId: entry.slug,
      brand: entry.brand,
      product: entry.product,
      variety: entry.variety,
      size: entry.size,
      unit: entry.unit,
      upc: entry.upc,
      gtin: entry.gtin,
    });
  }

  const confidenceRank: Record<RestockConfidence, number> = { high: 0, medium: 1, low: 2 };
  return results.sort((a, b) => a.daysUntilDue - b.daysUntilDue || confidenceRank[a.confidence] - confidenceRank[b.confidence]);
};

/** Keys to write when hiding or snoozing a family: its family key and every member's key. */
export const restockSettingKeys = (item: RestockItem) => [item.key, ...item.memberKeys];

const storeKey = (name?: string | null) => (name || '').split(',')[0].trim().toLowerCase();

/** Whether the user has bought this family at the given store before. */
export const boughtAtStore = (item: RestockItem, storeName?: string | null) => {
  const target = storeKey(storeName);
  return !!target && item.stores.some(s => storeKey(s) === target);
};

/**
 * Suggested Essentials: what to add to this trip. Families due within a few
 * days with nothing from the family already in the basket; when a store is
 * picked, only the ones the user buys there.
 */
export const selectBasketSuggestions = (
  items: RestockItem[],
  basket: { canonicalId?: string | null; name: string; product?: string | null; metadata?: Record<string, any> | null }[],
  storeName?: string | null,
): RestockItem[] => {
  const ids = new Set(basket.flatMap(b => [b.canonicalId, b.name].filter(Boolean) as string[]));
  const nouns = new Set(basket.map(b => familyNoun(b.metadata?.family || b.product || b.name)).filter(Boolean));
  return items
    .filter(i => i.daysUntilDue <= BASKET_DUE_WINDOW_DAYS)
    .filter(i => !nouns.has(i.family) && !i.memberKeys.some(k => ids.has(k)) && !(i.canonicalId && ids.has(i.canonicalId)))
    .filter(i => !storeName || boughtAtStore(i, storeName))
    .slice(0, BASKET_LIMIT);
};
